import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
    Controller,
    ControllerProps,
    FieldPath,
    FieldValues,
    FormProvider,
    useFormContext,
    useFormState,
} from "react-hook-form";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { cva } from "class-variance-authority";
import { CircleAlert, CircleCheck } from "lucide-react";
import { AnimateScaleUp } from "./animations";

const Form = FormProvider;

type FormFieldContextValue<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = { name: TName };

const FormFieldContext = React.createContext<FormFieldContextValue>(
    {} as FormFieldContextValue,
);

const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    ...props
}: ControllerProps<TFieldValues, TName>) => {
    return (
        <FormFieldContext value={{ name: props.name }}>
            <Controller {...props} />
        </FormFieldContext>
    );
};

const useFormField = () => {
    const fieldContext = React.use(FormFieldContext);
    const itemContext = React.use(FormItemContext);
    const { control, getFieldState } = useFormContext();
    const formState = useFormState({
        control,
    });

    const fieldState = getFieldState(fieldContext.name, formState);

    if (!fieldContext) {
        throw new Error("useFormField should be used within <FormField>");
    }

    const { id } = itemContext;

    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    };
};

type FormItemContextValue = { id: string };

const FormItemContext = React.createContext<FormItemContextValue>(
    {} as FormItemContextValue,
);

const FormItem = ({ ...props }: React.HTMLAttributes<HTMLDivElement>) => {
    const id = React.useId();

    return (
        <FormItemContext value={{ id }}>
            <div {...props} />
        </FormItemContext>
    );
};
FormItem.displayName = "FormItem";

const FormLabel = ({
    className,
    required,
    ...props
}: React.ComponentProps<typeof LabelPrimitive.Root> & {
    required?: boolean;
}) => {
    const { error, formItemId } = useFormField();

    return (
        <div>
            <Label
                className={cn(error && "text-danger", className)}
                htmlFor={formItemId}
                {...props}
            />
            {required && (
                <span className="ml-narrow font-body text-default text-danger font-bold">
                    *
                </span>
            )}
        </div>
    );
};
FormLabel.displayName = "FormLabel";

const FormControl = (props: React.ComponentProps<typeof Slot>) => {
    const { error, formItemId, formDescriptionId, formMessageId } =
        useFormField();

    return (
        <Slot
            id={formItemId}
            aria-describedby={
                !error
                    ? `${formDescriptionId}`
                    : `${formDescriptionId} ${formMessageId}`
            }
            aria-invalid={!!error}
            {...props}
        />
    );
};
FormControl.displayName = "FormControl";

const FormDescription = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => {
    const { formDescriptionId } = useFormField();

    return (
        <p
            id={formDescriptionId}
            className={cn("text-small leading-small text-subtlest", className)}
            {...props}
        />
    );
};
FormDescription.displayName = "FormDescription";

const messageVariants = cva("text-small", {
    variants: { appearance: { success: "text-success", error: "text-danger" } },
});

const FormMessage = ({
    className,
    children,
    isError,
    ...props
}: React.HTMLAttributes<HTMLParagraphElement> & { isError?: boolean }) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : children;
    if (!body) {
        return null;
    }

    return (
        <AnimateScaleUp
            show
            animateOnMount
            className="gap-narrow flex items-center"
        >
            {error || isError ? (
                <CircleAlert className="text-danger h-5 w-5" />
            ) : (
                <CircleCheck className="text-success h-5 w-5" />
            )}
            <p
                id={formMessageId}
                className={cn(
                    messageVariants({
                        appearance: error || isError ? "error" : "success",
                    }),
                    className,
                )}
                {...props}
            >
                {body}
            </p>
        </AnimateScaleUp>
    );
};
FormMessage.displayName = "FormMessage";

export {
    useFormField,
    Form,
    FormItem,
    FormLabel,
    FormControl,
    FormDescription,
    FormMessage,
    FormField,
};
